// Sub-service pages under "Market & Customer Intelligence".
// Rendered by pages/ServiceDetailPage.jsx at
// /capabilities/market-customer-intelligence/<slug>

export const INTELLIGENCE_SUBPAGES = [
  {
    slug: "market-opportunity-sizing",
    image: "/photos/sub-market-opportunity-sizing.jpg",
    imageAlt: "Indian market analysts studying market-size charts and a map on a large screen",
    title: "Market Opportunity & Sizing",
    metaDescription:
      "Market sizing, TAM/SAM analysis, growth-pool identification and market-entry assessment — prioritise the opportunities worth pursuing.",
    heroSubhead: "Know where the growth is before you invest behind it.",
    heroParas: [
      "Market estimates can make almost any opportunity look attractive. The real question is how much of that opportunity your business can realistically access — and profitably win.",
      "BLEZZINGS helps businesses size markets, identify growth pools, assess commercial attractiveness and prioritise the opportunities worth pursuing.",
    ],
    heroCta: "Assess Your Market Opportunity",
    challengeLead: "A large market does not automatically mean a large opportunity for you.",
    challengeParas: [
      "Businesses often commit resources based on headline market sizes, analyst forecasts or broad category growth.",
      "But those numbers rarely answer the questions management actually needs answered:",
    ],
    challengeSymptoms: [
      "How large is the relevant market for our proposition?",
      "Which segments are growing fastest?",
      "Where are the highest-value opportunities?",
      "How much of the market is realistically addressable?",
      "Which geographies should we prioritise?",
      "How concentrated or fragmented is the market?",
      "What competitive barriers exist?",
      "Can we enter profitably?",
      "What would it take to win meaningful share?",
    ],
    challengeClosing:
      "A ₹10,000 crore category can still be a poor opportunity. A ₹500 crore niche can sometimes create considerably more value. The difference lies in understanding the accessible growth pool, not simply the headline market size.",
    perspectiveLead: "Market sizing should support an investment decision — not produce a impressive-looking number.",
    perspectiveBlocks: [
      { type: "paras", items: ["We distinguish between:"] },
      {
        type: "list",
        items: [
          "Market size — How large is the category?",
          "Addressable opportunity — Which part of that market can your business actually serve?",
          "Commercial attractiveness — Is that opportunity worth pursuing?",
          "Right to win — Why should your business be able to capture it?",
        ],
      },
      {
        type: "paras",
        items: [
          "That means evaluating market size alongside customer need, competitive intensity, pricing, channels, capabilities and economics.",
          "The goal is not merely to answer: “How big is the market?”",
        ],
      },
      { type: "highlight", text: "It is to answer: “Where should we invest — and where should we not?”" },
    ],
    transform: [
      { name: "Market Sizing", body: "Estimate current market value, volume and relevant sub-market opportunities." },
      { name: "TAM, SAM & Obtainable Opportunity", body: "Move from theoretical market potential toward the realistically accessible opportunity." },
      { name: "Category & Segment Attractiveness", body: "Evaluate growth, profitability, customer demand, competition and structural attractiveness." },
      { name: "Geographic Prioritisation", body: "Compare countries, states, cities and clusters to determine where expansion should begin." },
      { name: "Growth-Pool Identification", body: "Locate disproportionate pockets of future revenue and profit." },
      { name: "White-Space Assessment", body: "Identify underserved needs, customer groups, use cases, channels and price points." },
      { name: "Market-Entry Assessment", body: "Evaluate whether a market is attractive enough to enter and what barriers must be overcome." },
      { name: "Opportunity Prioritisation", body: "Rank opportunities using clear commercial criteria rather than management intuition alone." },
    ],
    approach: [
      { title: "Discover", body: "Gather market, customer, competitor, category, channel and economic evidence from primary and secondary sources." },
      { title: "Strategize", body: "Define the relevant market, identify growth pools and establish the criteria by which opportunities should be evaluated." },
      { title: "Design", body: "Build sizing models, opportunity maps, attractiveness frameworks and scenarios." },
      { title: "Activate", body: "Translate the analysis into market-entry, portfolio, investment and GTM priorities." },
      { title: "Optimize", body: "Update assumptions as new evidence emerges and refine investment decisions accordingly." },
    ],
    deliverIntro: "Depending on the engagement:",
    deliver: [
      "Market sizing model",
      "TAM / SAM / obtainable opportunity analysis",
      "Category and sub-category analysis",
      "Market growth forecast",
      "Demand-driver analysis",
      "Geographic opportunity map",
      "Market-attractiveness scorecard",
      "Growth-pool analysis",
      "White-space map",
      "Customer opportunity assessment",
      "Competitor and concentration analysis",
      "Market-entry feasibility assessment",
      "Scenario modelling",
      "Opportunity prioritisation matrix",
      "Investment recommendations",
      "Strategic implications and action roadmap",
    ],
    outcomesIntro: "Our work can help businesses:",
    outcomes: [
      "Allocate investment toward the highest-potential opportunities",
      "Avoid entering structurally unattractive markets",
      "Prioritise the right customer segments",
      "Accelerate geographic expansion",
      "Identify new categories and adjacencies",
      "Improve capital allocation",
      "Reduce uncertainty before major investments",
      "Build more credible business cases",
      "Focus management resources where they can create the greatest commercial value",
    ],
    impact: [
      { metric: "5× Sales Growth", body: "Relevant leadership experience includes helping build and scale a differentiated consumer business to a ₹150Cr+ annual run-rate and a Top 3 position among Amazon bestsellers." },
      { metric: "First Profit in a Decade", body: "Leadership experience also includes commercial turnaround work that moved a consumer-durables category to profitability for the first time since inception." },
    ],
    impactNote:
      "Selected outcomes were delivered by BLEZZINGS leadership across prior operating and consulting roles. Case relevance depends on engagement scope.",
    whyLead: "We don't size markets in isolation from the ability to win them.",
    whyParas: [
      "A market can be growing quickly and still be strategically unattractive.",
      "BLEZZINGS connects market intelligence with:",
      "This allows us to evaluate opportunity from a commercial and P&L perspective, rather than relying only on category growth projections.",
    ],
    whyListAfter: 1,
    whyList: [
      "Customer demand",
      "Competition",
      "Portfolio",
      "Pricing",
      "Channel economics",
      "Go-to-market",
      "Sales capability",
      "Marketing investment",
      "Profitability",
    ],
    whoLead: "This work is particularly valuable when you are:",
    whoBullets: [
      "Considering entry into a new category",
      "Evaluating a new country, state or geography",
      "Building a three-to-five-year growth strategy",
      "Assessing an adjacent market",
      "Creating an investment business case",
      "Prioritising expansion opportunities",
      "Launching a new business line",
      "Looking for the next source of growth",
      "Deciding between multiple market opportunities",
      "Challenging optimistic internal market assumptions",
    ],
    whoAudience:
      "Typically relevant to CEOs, founders, business heads, strategy teams, investors, category heads and P&L owners.",
    faqs: [
      { q: "What is market opportunity sizing?", a: "Market opportunity sizing estimates the commercial potential available within a defined market, category, geography, segment or customer group." },
      { q: "What is the difference between TAM, SAM and obtainable opportunity?", a: "TAM represents the broad theoretical market. SAM narrows that to the market your proposition can serve. Obtainable opportunity considers what your business can realistically capture based on competition, channels, capabilities and resources." },
      { q: "Where does market-size data come from?", a: "Depending on the market, we triangulate government data, industry sources, company information, trade data, expert interviews, customer research, channel evidence and bottom-up modelling." },
      { q: "Can BLEZZINGS size markets where reliable published data does not exist?", a: "Yes. In fragmented or emerging categories, bottom-up modelling and triangulation often produce better decision-quality estimates than relying on a single published source." },
      { q: "Can you also tell us whether we should enter the market?", a: "Yes. Market size is only one dimension. We can assess customer demand, competition, economics, entry barriers and your right to win before making a strategic recommendation." },
    ],
    insights: [
      "TAM Is Not Your Market: The Number That Actually Matters",
      "Big Market ≠ Good Market",
      "How to Identify the Growth Pools Worth Chasing",
      "Five Questions to Answer Before Entering a New Market",
      "Why Most Market-Entry Business Cases Are Too Optimistic",
    ],
    related: [
      { title: "Go-To-Market & Market Entry", body: "Translate attractive opportunities into a commercial market-entry plan.", to: "/capabilities/strategy-commercial-transformation/go-to-market-market-entry" },
      { title: "Product Portfolio Strategy", body: "Determine which products and categories deserve investment.", to: "/capabilities/strategy-commercial-transformation/product-portfolio-strategy" },
      { title: "Segmentation & Targeting", body: "Identify the customers behind the opportunity.", to: "/capabilities/market-customer-intelligence/segmentation-targeting" },
      { title: "Competitive Intelligence", body: "Understand who already controls the market and how they win.", to: "/capabilities/market-customer-intelligence/competitive-intelligence" },
    ],
    ctaHeading: "Before you invest in an opportunity, know how much of it you can actually win.",
    ctaBody:
      "BLEZZINGS can help you size the market, locate the real growth pools and determine whether the opportunity deserves your investment.",
    ctaLabel: "Assess Your Market Opportunity",
  },

  {
    slug: "customer-buyer-insights",
    image: "/photos/sub-customer-buyer-insights.jpg",
    imageAlt: "Indian researcher moderating a consumer focus-group discussion",
    title: "Customer, Consumer & Buyer Insights",
    metaDescription:
      "Customer, consumer and B2B buyer research that uncovers needs, motivations, purchase drivers, barriers and unmet opportunities.",
    heroSubhead: "Understand what customers do — and why they do it.",
    heroParas: [
      "Growth becomes easier when businesses understand not only who customers are, but what they need, value, fear, expect and choose.",
      "BLEZZINGS helps businesses uncover the needs, behaviours, motivations, barriers and unmet opportunities that shape customer decisions.",
    ],
    heroCta: "Understand Your Customers Better",
    challengeLead: "Businesses often know a lot about customers without truly understanding them.",
    challengeParas: [
      "Dashboards can tell you: what customers bought, where they bought, how often they bought, which campaign generated the click.",
      "But they rarely explain:",
    ],
    challengeSymptoms: [
      "Why did they choose you?",
      "Why did others choose a competitor?",
      "What problem are they really trying to solve?",
      "What do they value enough to pay for?",
      "What frustrations remain unresolved?",
      "Why do prospects hesitate?",
      "Which features matter — and which don't?",
      "What would make customers switch?",
      "How is behaviour changing?",
    ],
    challengeClosing: "When those questions remain unanswered, strategy gets built around assumptions.",
    perspectiveLead: "Customers rarely think in products. They think in needs, outcomes and trade-offs.",
    perspectiveBlocks: [
      {
        type: "paras",
        items: [
          "Businesses naturally view the market through categories, products and organisational structures.",
          "Customers do not.",
          "They evaluate choices through:",
        ],
      },
      { type: "highlight", text: "Needs. Problems. Context. Value. Risk. Convenience. Trust. Alternatives." },
      {
        type: "paras",
        items: [
          "Good customer research therefore goes beyond collecting opinions.",
          "It must identify the underlying drivers of behaviour — and determine what those insights mean for products, propositions, pricing, experiences, communications and growth.",
        ],
      },
    ],
    transform: [
      { name: "Needs & Unmet Needs", body: "Identify what customers are trying to achieve and where existing solutions fall short." },
      { name: "Motivations & Behaviour", body: "Understand the emotional, functional and commercial forces behind customer actions." },
      { name: "Purchase Drivers & Barriers", body: "Identify what increases likelihood of purchase — and what prevents conversion." },
      { name: "Usage & Occasion Intelligence", body: "Understand when, where, why and how products or services are used." },
      { name: "Perceptions & Associations", body: "Understand how customers perceive brands, categories, competitors and value." },
      { name: "Value Expectations", body: "Determine what customers believe constitutes value and where willingness to pay may exist." },
      { name: "B2B Buyer Intelligence", body: "Understand organisational needs, procurement criteria, decision processes and stakeholder priorities." },
      { name: "Opportunity Discovery", body: "Turn unmet needs and customer frustrations into product, proposition and growth opportunities." },
    ],
    approach: [
      { title: "Discover", body: "Gather existing customer data and define the unanswered commercial questions." },
      { title: "Strategize", body: "Identify the customer groups, hypotheses and decisions that the research needs to inform." },
      { title: "Design", body: "Select the right qualitative, quantitative and observational research methods." },
      { title: "Activate", body: "Translate customer evidence into product, proposition, brand, pricing, sales or marketing actions." },
      { title: "Optimize", body: "Track how customer needs and behaviour evolve and update the strategy accordingly." },
    ],
    deliver: [
      "Customer-insight diagnostic",
      "Qualitative research",
      "Quantitative research",
      "Depth-interview findings",
      "Focus-group synthesis",
      "Buyer interviews",
      "Usage and occasion analysis",
      "Needs hierarchy",
      "Jobs-to-be-done framework",
      "Pain-point map",
      "Purchase-driver analysis",
      "Barrier analysis",
      "Brand/category perception study",
      "Customer value framework",
      "B2B buying-criteria analysis",
      "Opportunity territories",
      "Strategic implications",
      "Prioritised recommendations",
    ],
    outcomesIntro: "Customer intelligence can help create:",
    outcomes: [
      "Stronger product-market fit",
      "Better value propositions",
      "Higher conversion",
      "More relevant innovation",
      "Stronger positioning",
      "Improved customer experience",
      "Better marketing communication",
      "Greater customer acquisition efficiency",
      "Higher willingness to pay",
      "Reduced product-development risk",
      "Stronger customer retention",
    ],
    impact: [
      { metric: "5× Sales Growth + 28% Improvement in Marketing ROI", body: "BLEZZINGS leadership has helped scale a differentiated consumer business fivefold while simultaneously improving marketing efficiency. The underlying principle is central to this capability: growth improves when the proposition, communication and commercial model are built around customer behaviour rather than internal assumptions." },
    ],
    impactNote: "Selected outcome delivered in a prior operating role.",
    whyLead: "We don't stop at what the customer said.",
    whyParas: [
      "Research can produce interesting findings without producing better decisions.",
      "BLEZZINGS asks one additional question throughout the engagement: “So what should the business do differently?”",
      "That may affect:",
      "Our advantage is not simply gathering customer insight. It is connecting customer truth to commercial action.",
    ],
    whyListAfter: 2,
    whyList: [
      "Product design",
      "Portfolio",
      "Pricing",
      "Positioning",
      "Sales approach",
      "Customer experience",
      "Channel",
      "Marketing communication",
      "Digital journeys",
    ],
    whoLead: "Particularly relevant when:",
    whoBullets: [
      "Sales are slowing and the reason is unclear",
      "A product is underperforming",
      "Customer conversion is weak",
      "You are developing a new product",
      "Management assumptions about customers need validating",
      "Brand positioning feels disconnected from customers",
      "You want to command a price premium",
      "Customer retention is deteriorating",
      "You need to understand B2B decision criteria",
      "A category is changing rapidly",
    ],
    whoAudience:
      "Typically relevant to CEOs, CMOs, product leaders, category heads, customer experience teams, innovation teams, business heads and sales leaders.",
    faqs: [
      { q: "What is customer insight research?", a: "Customer insight research identifies the needs, behaviours, motivations, perceptions and barriers that influence customer decisions." },
      { q: "What is the difference between customer data and customer insight?", a: "Data tells you what happened. Insight helps explain why it happened and what the business should do because of it." },
      { q: "Does BLEZZINGS conduct both qualitative and quantitative research?", a: "Yes. The methodology depends on the decision. Qualitative research typically explores motivations and behaviours deeply; quantitative research helps measure and validate patterns across larger populations." },
      { q: "Can you conduct B2B customer research?", a: "Yes. We can interview customers, prospects, channel partners, procurement teams, decision makers and other stakeholders involved in B2B buying." },
      { q: "Can this research support product development?", a: "Yes. Customer intelligence can help identify unmet needs, prioritise features, refine propositions and reduce risk before investment." },
    ],
    insights: [
      "Customers Don't Buy Products. They Buy Progress.",
      "What Customers Say vs What Customers Actually Do",
      "Five Customer Questions Every Growth Strategy Should Answer",
      "Why Customer Surveys Often Produce Bad Strategy",
      "The Unmet Need Is Often Hiding in the Workaround",
    ],
    related: [
      { title: "Segmentation & Targeting", body: "Identify which customer groups offer the greatest commercial opportunity.", to: "/capabilities/market-customer-intelligence/segmentation-targeting" },
      { title: "Product Portfolio Strategy", body: "Translate customer needs into stronger product choices.", to: "/capabilities/strategy-commercial-transformation/product-portfolio-strategy" },
      { title: "Brand Strategy", body: "Build positioning around the value customers actually seek.", to: "/capabilities/brand-marketing-communications" },
      { title: "Digital & Commerce", body: "Translate behavioural insight into better acquisition and conversion journeys.", to: "/capabilities/digital-commerce" },
    ],
    ctaHeading: "Stop guessing what customers want.",
    ctaBody:
      "We can help uncover what truly drives customer choice — and translate that understanding into better products, propositions and commercial decisions.",
    ctaLabel: "Understand Your Customers Better",
  },

  {
    slug: "segmentation-targeting",
    image: "/photos/sub-segmentation-targeting.jpg",
    imageAlt: "Indian strategists arranging customer persona cards across a large worktable",
    title: "Segmentation & Targeting",
    metaDescription:
      "Actionable customer segmentation, Ideal Customer Profiles and target prioritisation — focus investment on the customers most worth winning.",
    heroSubhead: "Not every customer is equally valuable — or equally worth pursuing.",
    heroParas: [
      "Growth becomes more efficient when businesses know which customers to prioritise, what those customers value and how the proposition should change for them.",
      "BLEZZINGS helps businesses create actionable customer segments and turn them into clear targeting priorities.",
    ],
    heroCta: "Identify Your Priority Customers",
    challengeLead: "When everyone is the target customer, nobody really is.",
    challengeParas: [
      "Businesses frequently define target markets using broad descriptions: urban consumers, millennials, SMEs, premium customers, working women, large enterprises.",
      "These labels may describe customers, but they rarely explain why those customers behave differently or how the business should treat them differently.",
      "Weak segmentation creates:",
    ],
    challengeSymptoms: [
      "Generic propositions",
      "Fragmented marketing spend",
      "Poor lead quality",
      "High acquisition costs",
      "Undifferentiated products",
      "Inefficient sales coverage",
      "Low customer relevance",
      "Conflicting internal definitions of the ideal customer",
    ],
    challengeClosing: "The result is often more activity — without greater precision.",
    perspectiveLead: "A useful segment must change a business decision.",
    perspectiveBlocks: [
      {
        type: "paras",
        items: [
          "If two customer groups behave differently but your product, pricing, message, channel or sales approach remains identical, the segmentation has limited commercial value.",
          "We believe useful segmentation should be:",
        ],
      },
      {
        type: "list",
        items: [
          "Distinct — segments behave meaningfully differently.",
          "Measurable — their size and commercial potential can be estimated.",
          "Reachable — the business can identify and access them.",
          "Valuable — they represent meaningful commercial opportunity.",
          "Actionable — strategy can change because the segment exists.",
        ],
      },
      {
        type: "paras",
        items: [
          "The purpose of segmentation is not to produce more personas.",
          "It is to concentrate resources behind the customers most worth winning.",
        ],
      },
    ],
    transform: [
      { name: "Customer Segmentation", body: "Identify groups based on needs, behaviours, value or decision drivers." },
      { name: "Consumer Segmentation", body: "Move beyond demographics toward commercially meaningful need states and behaviours." },
      { name: "B2B Segmentation", body: "Segment organisations by potential, needs, complexity, fit and buying behaviour." },
      { name: "Ideal Customer Profile", body: "Define the customers most likely to generate sustainable commercial value." },
      { name: "Segment Sizing", body: "Estimate the scale and revenue potential of each segment." },
      { name: "Segment Attractiveness", body: "Compare growth, profitability, fit, accessibility and competitive intensity." },
      { name: "Target Prioritisation", body: "Determine which segments deserve primary, secondary or limited investment." },
      { name: "Segment-Specific Strategy", body: "Adapt propositions, products, pricing, sales and marketing by segment." },
    ],
    approach: [
      { title: "Discover", body: "Analyse customers, transactions, behaviours, needs, value, channel usage and existing segmentation." },
      { title: "Strategize", body: "Determine the dimensions that best explain commercial differences between customers." },
      { title: "Design", body: "Create, size and profile segments and develop targeting priorities." },
      { title: "Activate", body: "Translate the segmentation into proposition, portfolio, sales, marketing and channel actions." },
      { title: "Optimize", body: "Track segment performance and update the model as customer behaviour changes." },
    ],
    deliver: [
      "Segmentation diagnostic",
      "Customer research",
      "Segmentation variables",
      "Behavioural and attitudinal segmentation",
      "Customer cluster model",
      "B2B account segmentation",
      "Ideal Customer Profile",
      "Segment profiles",
      "Segment sizing",
      "Segment value analysis",
      "Segment-attractiveness matrix",
      "Priority target definition",
      "Persona/need-state profiles",
      "Segment-specific proposition recommendations",
      "Channel and communication implications",
      "Sales/account prioritisation rules",
      "Activation roadmap",
    ],
    outcomesIntro: "Stronger segmentation can create:",
    outcomes: [
      "Better allocation of marketing spend",
      "Higher quality leads",
      "Improved customer acquisition efficiency",
      "Higher sales productivity",
      "Better conversion",
      "More relevant propositions",
      "Stronger pricing architecture",
      "Better product-market fit",
      "Improved customer retention",
      "Greater clarity across sales and marketing",
      "Reduced investment in low-value customers",
    ],
    impact: [
      { metric: "Scaling a Differentiated Consumer Proposition", body: "BLEZZINGS leadership experience includes scaling a differentiated consumer business fivefold and building one of the leading products in its category on a major marketplace. The lesson is fundamental to segmentation: growth accelerates when a business becomes clear about which customers it is serving, what they value and why the proposition deserves to win." },
    ],
    impactNote: "Selected outcome delivered in a prior operating role.",
    whyLead: "We build segments that sales, marketing and product teams can actually use.",
    whyParas: [
      "Many segmentation studies create compelling customer descriptions but struggle during implementation.",
      "BLEZZINGS connects segmentation directly to:",
      "The output is not simply a segmentation report. It is a commercial prioritisation system.",
    ],
    whyListAfter: 1,
    whyList: [
      "Portfolio choices",
      "Value propositions",
      "Pricing",
      "Sales coverage",
      "Channel strategy",
      "Lead qualification",
      "Brand positioning",
      "Media targeting",
      "Customer retention",
    ],
    whoLead: "Relevant when:",
    whoBullets: [
      "Marketing targets audiences too broadly",
      "Customer acquisition costs are rising",
      "Sales teams chase low-potential prospects",
      "Different departments define the target customer differently",
      "You need an Ideal Customer Profile",
      "You are entering a new market",
      "You serve multiple customer types",
      "Your proposition feels too generic",
      "Product development lacks customer prioritisation",
      "Your highest-value customers are not clearly understood",
    ],
    whoAudience:
      "Typically relevant to CMOs, CEOs, sales leaders, business heads, growth teams, category leaders, product teams and revenue leaders.",
    faqs: [
      { q: "What is customer segmentation?", a: "Customer segmentation divides a market into distinct groups whose needs, behaviours, value or decision criteria differ in commercially meaningful ways." },
      { q: "Is demographic segmentation sufficient?", a: "Usually not. Demographics can help describe customers but do not always explain behaviour. Strong segmentation often incorporates needs, attitudes, behaviours, value and context." },
      { q: "What is an Ideal Customer Profile?", a: "An ICP describes the characteristics of customers that have the strongest fit with the company's proposition and are most likely to create sustainable value." },
      { q: "Can BLEZZINGS segment B2B markets?", a: "Yes. B2B segmentation may consider organisation size, industry, potential, buying behaviour, needs, channel preferences, profitability and strategic fit." },
      { q: "How does segmentation affect marketing?", a: "Segmentation can influence media targeting, messages, offers, content, customer journeys, channel mix and acquisition strategy." },
    ],
    insights: [
      "Your Target Market Is Probably Too Broad",
      "Segmentation ≠ Personas",
      "How to Build an Ideal Customer Profile That Sales Will Actually Use",
      "The Most Valuable Customer Is Not Always the Biggest Customer",
      "Why Demographics Are Usually a Weak Growth Strategy",
    ],
    related: [
      { title: "Customer, Consumer & Buyer Insights", body: "Understand the needs and behaviours behind meaningful segments.", to: "/capabilities/market-customer-intelligence/customer-buyer-insights" },
      { title: "Go-To-Market & Market Entry", body: "Build GTM around priority customers.", to: "/capabilities/strategy-commercial-transformation/go-to-market-market-entry" },
      { title: "Sales Transformation", body: "Align account coverage and sales effort with customer value.", to: "/capabilities/strategy-commercial-transformation/sales-transformation" },
      { title: "Digital & Commerce", body: "Translate segmentation into acquisition and lifecycle targeting.", to: "/capabilities/digital-commerce" },
    ],
    ctaHeading: "Focus your growth investment on the customers most worth winning.",
    ctaBody:
      "We can help identify, size and prioritise your most valuable customer segments — and translate them into clear commercial action.",
    ctaLabel: "Identify Your Priority Customers",
  },

  {
    slug: "competitive-intelligence",
    image: "/photos/sub-competitive-intelligence.jpg",
    imageAlt: "Indian analysts comparing competing products side by side on a table",
    title: "Competitive Intelligence",
    metaDescription:
      "Competitive intelligence that decodes how competitors win — strategy, pricing, channels, capabilities and vulnerabilities.",
    heroSubhead: "Know how competitors win — so you can choose how to beat them.",
    heroParas: [
      "Competitor monitoring tells you what others are doing.",
      "Competitive intelligence explains why they are doing it, how their commercial system works, where they are vulnerable and what it means for your strategy.",
    ],
    heroCta: "Decode Your Competitive Landscape",
    challengeLead: "Most businesses know their competitors. Far fewer understand them.",
    challengeParas: [
      "Competitor reviews often stop at: products, prices, advertising, websites, market share.",
      "But competitive advantage sits deeper.",
      "To understand why a competitor is winning, you may need to examine:",
    ],
    challengeSymptoms: [
      "Portfolio strategy",
      "Cost structure",
      "Channel economics",
      "Distribution reach",
      "Sales model",
      "Customer proposition",
      "Pricing architecture",
      "Digital capabilities",
      "Brand strength",
      "Customer perceptions",
      "Organisational capabilities",
      "Investment priorities",
    ],
    challengeClosing:
      "Without this context, companies often react tactically to competitors rather than developing a differentiated strategy.",
    perspectiveLead: "Competitive intelligence should improve your choices — not make you imitate competitors.",
    perspectiveBlocks: [
      {
        type: "paras",
        items: ["The goal is not to create a 100-slide competitor report.", "It is to answer:"],
      },
      {
        type: "list",
        items: [
          "What game are competitors playing?",
          "Why are they winning or losing?",
          "Where are they structurally advantaged?",
          "Where are they vulnerable?",
          "What should we do differently?",
        ],
      },
      {
        type: "paras",
        items: [
          "The best competitive strategy often comes not from matching the market leader, but from identifying where not to compete on their terms.",
        ],
      },
    ],
    transform: [
      { name: "Competitor Landscape", body: "Identify direct, indirect and emerging competitors." },
      { name: "Business Model Intelligence", body: "Understand how competitors create and capture value." },
      { name: "Portfolio Intelligence", body: "Compare categories, products, innovation pipelines and assortment architecture." },
      { name: "Pricing Intelligence", body: "Analyse price positioning, promotions, pack architecture and discount behaviour." },
      { name: "Channel Intelligence", body: "Understand distribution, marketplace, retail, D2C and partner models." },
      { name: "Brand & Communication Intelligence", body: "Decode positioning, value propositions, messaging and communication territories." },
      { name: "Digital Intelligence", body: "Assess online presence, acquisition activity, commerce execution and customer experience." },
      { name: "Capability Benchmarking", body: "Compare sales, marketing, service, distribution and operational capabilities." },
      { name: "Competitive Vulnerability", body: "Identify unmet customer needs, weak spots and strategic gaps." },
    ],
    approach: [
      { title: "Discover", body: "Collect competitor, customer, channel, financial, digital and market evidence." },
      { title: "Strategize", body: "Define the competitor set and the strategic questions that matter most." },
      { title: "Design", body: "Build comparative frameworks across strategy, propositions, economics and capabilities." },
      { title: "Activate", body: "Translate intelligence into differentiated choices across portfolio, pricing, GTM, brand and sales." },
      { title: "Optimize", body: "Track meaningful competitor moves and refine strategy as the landscape evolves." },
    ],
    deliver: [
      "Competitive landscape",
      "Competitor prioritisation",
      "Competitor profiles",
      "Strategy deep dives",
      "Business-model comparison",
      "Financial benchmarking",
      "Portfolio benchmarking",
      "Pricing and promotion analysis",
      "Channel benchmarking",
      "Distribution assessment",
      "Brand-positioning map",
      "Messaging analysis",
      "Digital and commerce benchmark",
      "Customer review analysis",
      "Capability scorecard",
      "Competitive advantage map",
      "Vulnerability analysis",
      "Strategic implications and response options",
    ],
    outcomesIntro: "Competitive intelligence can help:",
    outcomes: [
      "Improve differentiation",
      "Identify competitive white spaces",
      "Strengthen pricing decisions",
      "Anticipate competitor responses",
      "Improve market-entry strategy",
      "Identify channel opportunities",
      "Refine positioning",
      "Strengthen product strategy",
      "Prioritise capability investments",
      "Reduce reactive decision-making",
      "Identify opportunities competitors are overlooking",
    ],
    impact: [
      { metric: "Commercial Turnaround to First Profit in a Decade", body: "Relevant leadership experience includes transforming a loss-making consumer-durables category to profitability while competing in an established and highly contested market." },
      { metric: "+66% Sales Growth with Improved Profitability", body: "Leadership experience also includes rebuilding an e-commerce business in a competitive consumer category while increasing sales and improving EBITDA by nine percentage points." },
    ],
    impactNote: "Selected outcomes delivered across prior operating roles.",
    whyLead: "We analyse competitors as commercial systems, not collections of marketing activity.",
    whyParas: [
      "A competitor's price may make little sense until you understand:",
      "Because BLEZZINGS works across strategy, pricing, sales, channels, brand and digital commerce, we can connect these signals into a coherent picture.",
      "That makes the intelligence more useful than traditional competitor monitoring.",
    ],
    whyListAfter: 0,
    whyList: [
      "Their channel economics",
      "Portfolio architecture",
      "Customer acquisition model",
      "Cost structure",
      "Brand strength",
      "Distribution",
      "Commercial objectives",
    ],
    whoLead: "Particularly relevant when:",
    whoBullets: [
      "A competitor is taking share",
      "A new player has entered the category",
      "You are entering an unfamiliar market",
      "Price competition is intensifying",
      "Your brand differentiation is weakening",
      "You need to benchmark commercial performance",
      "New business models are disrupting the market",
      "Management needs a strategic competitor review",
      "Competitors appear to be growing faster",
      "You need to determine where to attack or defend",
    ],
    whoAudience:
      "Typically relevant to CEOs, business heads, strategy leaders, category leaders, CMOs, sales heads and investors.",
    faqs: [
      { q: "What is competitive intelligence?", a: "Competitive intelligence is the structured collection and analysis of information about competitors, markets and customer behaviour to support strategic and commercial decisions." },
      { q: "Is competitive intelligence just competitor benchmarking?", a: "No. Benchmarking compares performance or practices. Competitive intelligence goes further by interpreting competitor strategy, capabilities, economics and likely implications." },
      { q: "What information can BLEZZINGS analyse?", a: "Depending on availability and relevance, we may analyse public information, market data, customer research, channel evidence, digital signals, pricing, financial information and expert perspectives." },
      { q: "Can you help us respond to a new competitor?", a: "Yes. We can assess the competitor's proposition, likely target customers, economics, capabilities and vulnerabilities before defining strategic response options." },
      { q: "Can competitive intelligence support pricing?", a: "Yes. Competitive pricing analysis is particularly useful when combined with customer value, portfolio positioning and channel economics." },
    ],
    insights: [
      "Don't Copy the Market Leader",
      "Market Leaders Lose Because They're Right for Too Long",
      "Competitor Benchmarking Is Not Competitive Strategy",
      "How to Find a Competitor's Real Vulnerability",
      "The Next Market Leader Usually Solves a Different Problem",
    ],
    related: [
      { title: "Market Opportunity & Sizing", body: "Understand the attractiveness of the market competitors are fighting for.", to: "/capabilities/market-customer-intelligence/market-opportunity-sizing" },
      { title: "Pricing & Revenue Management", body: "Translate competitor pricing intelligence into better commercial choices.", to: "/capabilities/strategy-commercial-transformation/pricing-revenue-management" },
      { title: "Product Portfolio Strategy", body: "Identify portfolio gaps and opportunities.", to: "/capabilities/strategy-commercial-transformation/product-portfolio-strategy" },
      { title: "Brand Strategy", body: "Build differentiated positioning instead of competing through imitation.", to: "/capabilities/brand-marketing-communications" },
    ],
    ctaHeading: "Don't react to competitors. Understand them first.",
    ctaBody:
      "We can help decode how competitors win, where they are vulnerable and what those insights mean for your next strategic move.",
    ctaLabel: "Decode Your Competitive Landscape",
  },

  {
    slug: "customer-decision-journey-intelligence",
    image: "/photos/sub-customer-decision-journey-intelligence.jpg",
    imageAlt: "Indian team mapping a customer decision journey with sticky notes on a wall",
    title: "Customer Decision & Journey Intelligence",
    metaDescription:
      "Map how customers discover, evaluate, decide, buy and stay loyal — find the moments that increase or destroy conversion.",
    heroSubhead: "Understand what happens between interest and purchase.",
    heroParas: [
      "Customers rarely move through the buying journey the way businesses organise their funnels.",
      "BLEZZINGS maps how customers discover, evaluate, compare, decide, buy and remain loyal — revealing the moments that increase or destroy conversion.",
    ],
    heroCta: "Map Your Customer Decision Journey",
    challengeLead: "Every lost customer disappears somewhere in the journey.",
    challengeParas: [
      "Businesses invest heavily in awareness and lead generation yet frequently know surprisingly little about what happens next.",
      "Customers may:",
    ],
    challengeSymptoms: [
      "Fail to understand the proposition",
      "Distrust the brand",
      "Compare alternatives",
      "Visit marketplaces",
      "Ask friends or colleagues",
      "Speak to a salesperson",
      "Encounter a poor review",
      "Find pricing confusing",
      "Face friction in purchase",
      "Delay the decision",
      "Choose the safest option rather than the best one",
    ],
    challengeClosing:
      "Traditional funnel reporting shows where customers drop. Decision intelligence seeks to understand why.",
    perspectiveLead: "The customer journey is not a funnel. It is a sequence of decisions.",
    perspectiveBlocks: [
      {
        type: "paras",
        items: [
          "Customers move forward only when enough uncertainty has been removed.",
          "At every stage they are asking different questions:",
        ],
      },
      {
        type: "list",
        items: [
          "Is this relevant to me?",
          "Can I trust this?",
          "Is it better than the alternatives?",
          "Is it worth the price?",
          "What could go wrong?",
          "Why should I act now?",
        ],
      },
      {
        type: "paras",
        items: [
          "Growth therefore requires more than generating traffic.",
          "It requires understanding — and influencing — the decision architecture behind conversion.",
        ],
      },
    ],
    transform: [
      { name: "Decision-Journey Mapping", body: "Understand how customers move from trigger to purchase and post-purchase behaviour." },
      { name: "Purchase Drivers", body: "Identify the criteria that matter most in customer decisions." },
      { name: "Barriers & Objections", body: "Understand the doubts, risks and friction that stop purchase." },
      { name: "Touchpoint Intelligence", body: "Determine which interactions have the greatest influence on choice." },
      { name: "Channel Behaviour", body: "Understand how customers move between online, offline, direct and partner channels." },
      { name: "Information-Seeking Behaviour", body: "Identify the content, reviews, recommendations and proof customers seek." },
      { name: "B2B Buying Journeys", body: "Map decision makers, influencers, gatekeepers, procurement and buying committees." },
      { name: "Loyalty & Switching", body: "Understand what drives repeat purchase, retention and competitor switching." },
    ],
    approach: [
      { title: "Discover", body: "Collect customer, behavioural, sales, digital and journey evidence." },
      { title: "Strategize", body: "Identify the decision stages and commercial questions that matter most." },
      { title: "Design", body: "Map triggers, touchpoints, drivers, barriers, stakeholders and moments of truth." },
      { title: "Activate", body: "Redesign propositions, communications, sales interactions, digital journeys and experiences around customer decisions." },
      { title: "Optimize", body: "Track conversion, friction and behaviour as the new journey is implemented." },
    ],
    deliver: [
      "Customer decision journey",
      "Path-to-purchase map",
      "Trigger analysis",
      "Purchase-criteria hierarchy",
      "Barrier and objection map",
      "Touchpoint analysis",
      "Channel interaction map",
      "Search and information-behaviour analysis",
      "Review and social-proof analysis",
      "B2B stakeholder map",
      "Buying-committee analysis",
      "Sales interaction insights",
      "Moments-of-truth framework",
      "Journey friction analysis",
      "Conversion opportunity map",
      "Strategic recommendations",
      "Journey activation roadmap",
    ],
    outcomesIntro: "This work can help improve:",
    outcomes: [
      "Conversion rates",
      "Lead-to-sale performance",
      "Customer acquisition efficiency",
      "Digital conversion",
      "Sales effectiveness",
      "Customer experience",
      "Proposition clarity",
      "Content effectiveness",
      "Trust and credibility",
      "Retention and loyalty",
      "Marketing and sales alignment",
    ],
    impact: [
      { metric: "28% Improvement in Marketing ROI", body: "Relevant leadership experience includes materially improving marketing efficiency while scaling a consumer business." },
      { metric: "Large-Scale Customer Experience Transformation", body: "Leadership experience also includes transformation affecting more than 4,000 corporate and franchise locations across international markets." },
    ],
    impactNote: "Selected outcomes delivered across prior operating and consulting roles.",
    whyLead: "We connect the customer journey to the commercial system behind it.",
    whyParas: [
      "Customer journey maps often become visually impressive posters.",
      "BLEZZINGS focuses instead on where the journey should change:",
      "Because we can work across those functions, journey intelligence can move directly from insight into activation.",
    ],
    whyListAfter: 1,
    whyList: [
      "Proposition",
      "Pricing",
      "Proof",
      "Content",
      "Sales process",
      "Channel",
      "E-commerce",
      "CRM",
      "Service",
      "Retention",
    ],
    whoLead: "Relevant when:",
    whoBullets: [
      "Website traffic is high but conversion is poor",
      "Lead volume is healthy but sales closure is weak",
      "Customers take too long to decide",
      "Sales teams repeatedly face the same objections",
      "Customer acquisition costs are increasing",
      "Online and offline journeys are disconnected",
      "A product has high awareness but weak purchase",
      "B2B buying cycles are complex",
      "Customer retention is declining",
      "Teams disagree on why customers convert",
    ],
    whoAudience:
      "Typically relevant to CMOs, CROs, sales leaders, customer experience leaders, digital teams, product teams and business heads.",
    faqs: [
      { q: "What is customer journey research?", a: "Customer journey research examines how customers move from initial need or trigger through evaluation, purchase, usage, repeat purchase and advocacy." },
      { q: "How is a decision journey different from a marketing funnel?", a: "Funnels describe business-defined stages. Decision journeys focus on how customers actually think, behave and move between options." },
      { q: "Can you map B2B buying journeys?", a: "Yes. B2B journey research can identify users, influencers, decision makers, procurement stakeholders, approval stages and buying criteria." },
      { q: "Can journey research improve conversion?", a: "Yes. By identifying barriers, missing information, trust gaps and friction, businesses can redesign experiences around what customers require to progress." },
      { q: "Do you analyse digital behaviour as part of the journey?", a: "Where relevant, yes. Search behaviour, website interactions, marketplace behaviour, reviews and digital touchpoints may all contribute useful evidence." },
    ],
    insights: [
      "The Customer Journey Is Not a Funnel",
      "Why Customers Don't Buy Even When They Like the Product",
      "Every Conversion Problem Is a Decision Problem",
      "The Hidden Questions Customers Ask Before They Buy",
      "Why B2B Buying Journeys Are Getting Harder to Control",
    ],
    related: [
      { title: "Customer, Consumer & Buyer Insights", body: "Understand the needs and motivations behind customer decisions.", to: "/capabilities/market-customer-intelligence/customer-buyer-insights" },
      { title: "Brand, Marketing & Communications", body: "Strengthen propositions, proof and communication across the journey.", to: "/capabilities/brand-marketing-communications" },
      { title: "Sales Transformation", body: "Improve sales interactions and conversion.", to: "/capabilities/strategy-commercial-transformation/sales-transformation" },
      { title: "Digital & Commerce", body: "Remove digital friction and improve acquisition, commerce and retention.", to: "/capabilities/digital-commerce" },
    ],
    ctaHeading: "Find out what is helping customers move forward — and what is stopping them.",
    ctaBody:
      "We can help map the real decision journey and identify the changes most likely to improve conversion.",
    ctaLabel: "Map Your Customer Decision Journey",
  },

  {
    slug: "trends-foresight-growth-opportunities",
    image: "/photos/sub-trends-foresight-growth-opportunities.jpg",
    imageAlt: "Indian strategy team reviewing trend reports and emerging-market imagery",
    title: "Trends, Foresight & Growth Opportunities",
    metaDescription:
      "Strategic foresight that turns emerging customer, category, technology and channel shifts into future growth opportunities.",
    heroSubhead: "See where the market is moving — before the opportunity becomes obvious.",
    heroParas: [
      "Today's growth strategy cannot be built only on yesterday's market data.",
      "BLEZZINGS helps businesses identify emerging shifts in customers, categories, technology, channels and competition — and translate those signals into future growth opportunities.",
    ],
    heroCta: "Explore Your Next Growth Opportunities",
    challengeLead: "Most companies recognise trends only after they become mainstream.",
    challengeParas: [
      "By then: competitors have entered, customer expectations have shifted, new channels have gained power, pricing models have changed, new business models have emerged, category boundaries have moved, and the easiest opportunities have disappeared.",
      "The challenge is not spotting every trend. There is too much noise.",
      "The challenge is distinguishing between:",
    ],
    challengeSymptoms: [
      "A fad",
      "A meaningful behavioural shift",
      "A structural market change",
      "A commercially actionable growth opportunity",
    ],
    challengeClosing: "Knowing the difference is what separates foresight from noise.",
    perspectiveLead: "A trend matters only when it changes where growth will come from.",
    perspectiveBlocks: [
      {
        type: "paras",
        items: [
          "We do not believe foresight should be a collection of provocative future predictions.",
          "Useful foresight connects:",
        ],
      },
      { type: "highlight", text: "Signals → Shifts → Implications → Opportunities → Strategic Choices" },
      { type: "paras", items: ["That means asking:"] },
      {
        type: "list",
        items: [
          "What is changing?",
          "Why is it changing?",
          "How quickly could it scale?",
          "Which customers will be affected?",
          "Which categories will gain or lose?",
          "What new needs will emerge?",
          "What capabilities will become important?",
          "Where could new revenue and profit pools appear?",
        ],
      },
      {
        type: "paras",
        items: [
          "The objective is not to predict the future perfectly.",
          "It is to help businesses become better prepared for multiple plausible futures.",
        ],
      },
    ],
    transform: [
      { name: "Consumer & Customer Trends", body: "Track meaningful shifts in needs, values and behaviour." },
      { name: "Category Evolution", body: "Understand how category structures, occasions and expectations are changing." },
      { name: "Technology & Business-Model Shifts", body: "Evaluate emerging technologies and new ways value may be created or delivered." },
      { name: "Channel Evolution", body: "Understand how retail, marketplaces, D2C, social commerce and other routes-to-market are evolving." },
      { name: "Competitive Disruption", body: "Identify emerging players and models capable of changing category economics." },
      { name: "Scenario Planning", body: "Develop plausible future scenarios and determine strategic implications." },
      { name: "Emerging Growth Spaces", body: "Identify needs, customer groups, categories and business models with future potential." },
      { name: "Opportunity Prioritisation", body: "Separate commercially meaningful opportunities from interesting but low-value trends." },
    ],
    approach: [
      { title: "Discover", body: "Scan customer, category, technology, competitor, economic and behavioural signals." },
      { title: "Strategize", body: "Identify the structural shifts most relevant to the business and its future growth." },
      { title: "Design", body: "Develop scenarios, opportunity spaces and strategic response options." },
      { title: "Activate", body: "Translate priority opportunities into portfolio, innovation, capability and GTM initiatives." },
      { title: "Optimize", body: "Monitor leading indicators and update the strategy as uncertainty resolves." },
    ],
    deliver: [
      "Trend landscape",
      "Signal scan",
      "Consumer shift analysis",
      "Category evolution map",
      "Technology-impact assessment",
      "Channel-future assessment",
      "Emerging competitor map",
      "Scenario frameworks",
      "Future customer-needs map",
      "Growth-theme framework",
      "Opportunity territories",
      "White-space analysis",
      "Strategic implications",
      "Opportunity-attractiveness assessment",
      "Priority growth bets",
      "Innovation themes",
      "Capability implications",
      "Future growth roadmap",
    ],
    outcomesIntro: "Foresight can help organisations:",
    outcomes: [
      "Identify emerging growth opportunities earlier",
      "Strengthen innovation pipelines",
      "Reduce strategic surprise",
      "Build more resilient growth strategies",
      "Anticipate changing customer expectations",
      "Prepare for channel disruption",
      "Identify attractive adjacencies",
      "Prioritise future capability investments",
      "Avoid over-investment in short-lived trends",
      "Build stronger long-term portfolio choices",
    ],
    impact: [
      { metric: "Building and Scaling New Growth Engines", body: "Relevant leadership experience includes building and scaling differentiated new commercial propositions, driving fivefold growth in a consumer business and transforming major channels from loss-making to profitable growth. These experiences shape our approach to foresight: opportunity becomes valuable only when a business can turn it into a viable commercial model." },
    ],
    impactNote: "Selected outcomes delivered across prior operating roles.",
    whyLead: "We connect foresight with the economics of making it real.",
    whyParas: [
      "Trend reports often finish with a list of things businesses should watch.",
      "BLEZZINGS goes further. We evaluate opportunities through:",
      "This helps management distinguish between a trend worth observing and an opportunity worth funding.",
    ],
    whyListAfter: 1,
    whyList: [
      "Customer relevance",
      "Market potential",
      "Competitive intensity",
      "Revenue potential",
      "Margin potential",
      "Capability requirements",
      "Investment requirement",
      "Strategic fit",
      "Right to win",
    ],
    whoLead: "Relevant when:",
    whoBullets: [
      "You are building a three-to-five-year strategy",
      "Current growth engines are maturing",
      "You need the next source of growth",
      "Customer behaviour is changing quickly",
      "Technology is disrupting the category",
      "New competitors are emerging",
      "Traditional channels are losing relevance",
      "You need to strengthen the innovation pipeline",
      "Management wants to evaluate adjacencies",
      "The future direction of the category is uncertain",
    ],
    whoAudience:
      "Typically relevant to CEOs, founders, strategy teams, innovation leaders, category heads, CMOs and business-unit leaders.",
    faqs: [
      { q: "What is strategic foresight?", a: "Strategic foresight is the systematic identification and interpretation of emerging signals and structural shifts to help organisations prepare for future scenarios and opportunities." },
      { q: "Is foresight the same as trend research?", a: "Trend research identifies changes. Strategic foresight evaluates their longer-term implications and helps determine what strategic response may be required." },
      { q: "Can you predict which trend will definitely succeed?", a: "No credible foresight process can eliminate uncertainty. The objective is to understand possible futures, identify leading indicators and prepare better strategic choices." },
      { q: "Can foresight help identify new products?", a: "Yes. Emerging needs, behaviours and technologies can reveal new product, service, category and business-model opportunities." },
      { q: "How far into the future do you look?", a: "The horizon depends on the industry and decision. Consumer categories may require a shorter horizon than infrastructure, mobility, healthcare or technology-driven industries." },
    ],
    insights: [
      "The Next Market Leader Solves a Different Problem",
      "Growth ≠ Scaling",
      "Why Today's Best Customer May Not Be Tomorrow's",
      "How to Separate a Trend from a Fad",
      "The Growth Opportunity Usually Appears Before the Market Data",
    ],
    related: [
      { title: "Market Opportunity & Sizing", body: "Quantify the commercial potential behind emerging growth spaces.", to: "/capabilities/market-customer-intelligence/market-opportunity-sizing" },
      { title: "Product Portfolio Strategy", body: "Translate future opportunities into portfolio choices.", to: "/capabilities/strategy-commercial-transformation/product-portfolio-strategy" },
      { title: "Go-To-Market & Market Entry", body: "Build the route from future opportunity to market execution.", to: "/capabilities/strategy-commercial-transformation/go-to-market-market-entry" },
      { title: "Brand, Marketing & Communications", body: "Adapt positioning as customer expectations evolve.", to: "/capabilities/brand-marketing-communications" },
    ],
    ctaHeading: "Your next growth engine may already be visible — if you know what signals to watch.",
    ctaBody:
      "We can help identify the shifts most likely to matter, translate them into commercial opportunities and determine where your business should place its next bets.",
    ctaLabel: "Explore Your Next Growth Opportunities",
  },
];
